// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, FormattedMessage } from "react-intl";
import { Button } from "react-bootstrap";

import { withRouter } from "view/reactrouterinterceptor";
import { ActionType, IAction, IActionTypeSelectGuiItem, raiseAction } from "actions";
import { Category, PowerViewMode } from "states/cachedstates";
import { IState } from "states/states";
import { getSiteSelection } from "guiutils";
import { MainPanel } from "types";
import EnergySourceSelect from "./EnergySourceSelect";
import TariffCircuitDropDown from "./TariffCircuitDropDown";
import { isSet } from "shared/utils/utils";

import FilterBadge from "./FilterBadge";
import {
  ISiteBarDispatch,
  ISiteBarProps,
  SiteBarComponent,
  mapDispatchToProps as siteBarDispatch,
  mapStateToProps as siteBarProps,
} from "./SiteBar";

import IconFilter from "icons/iconFilter";

import "./SiteBar.scss";
import TariffCircuitList from "model/tariffcircuitlist";

interface ITrendSiteBarProps extends ISiteBarProps {
  selectedCategory?: Category;
  currentPanel?: MainPanel;
  powerViewMode: PowerViewMode;
  showTariffSelection: boolean;
  hasCategories: boolean;
}

const mapStateToProps = (state: IState) => {
  const selection = getSiteSelection(state);
  const powerViewMode = selection?.powerViewMode ?? PowerViewMode.TABLE;
  const hasCategories = TariffCircuitList.hasCategories(state.para.parameter?.tariffline ?? []);

  const props: ITrendSiteBarProps = {
    ...siteBarProps(state),
    selectedCategory: selection?.category,
    currentPanel: state.router.panel,
    powerViewMode,
    showTariffSelection:
      powerViewMode !== PowerViewMode.TABLE || !isSet(state.gui.selectedTimestamp),
    hasCategories,
  };

  return props;
};

interface ITrendSiteBarDispatch extends ISiteBarDispatch {
  onShowTimeseries: () => void;
  onShowFilter: () => void;
  onClearFilter: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => ({
  ...siteBarDispatch(dispatch),
  onShowFilter: () => {
    dispatch(raiseAction(ActionType.ACT_SELECT_FILTERS));
  },
  onClearFilter: () => {
    const payload: IActionTypeSelectGuiItem = {
      selection: undefined,
    };
    dispatch(raiseAction(ActionType.ACT_SELECT_CATEGORY, payload));
  },
  onShowTimeseries: () => {
    dispatch(raiseAction(ActionType.ACT_SELECT_TIMESTAMP, undefined));
  },
});

const messages = defineMessages({
  searchPreamble: {
    id: "Search",
  },
  filterPreamble: {
    id: "Filter",
  },
  noCategories: {
    id: "Without mapping",
  },
});

class TrendSiteBarComponent extends SiteBarComponent<ITrendSiteBarProps & ITrendSiteBarDispatch> {
  public override renderBackButton() {
    if (this.props.showBackButton) {
      return (
        <Button
          id="navigate-back"
          variant="secondary"
          className="btn-header me-3"
          onClick={this.props.onShowTimeseries}
        >
          <FormattedMessage id="Back to Timeseries" />
        </Button>
      );
    }
    return null;
  }

  public override renderSelectionButtons() {
    return (
      <>
        <EnergySourceSelect className="me-3" allowAllEnergySources={false} />
        {this.props.showTariffSelection && <TariffCircuitDropDown />}
      </>
    );
  }

  public override renderFilterButtons() {
    if (this.props.currentPanel === MainPanel.TRENDMONITOR || this.props.hasCategories === false) {
      return null;
    }
    const filterBadgeVisible = this.props.selectedCategory !== undefined;
    const filterButton = (
      <div className="position-relative ms-2 d-inline-flex">
        <Button
          className={"btn-header"}
          variant=""
          onClick={() => {
            this.props.onShowFilter();
          }}
        >
          <IconFilter width={"20px"} height={"20px"} />
        </Button>
        {filterBadgeVisible && (
          <span className="filter-active-indicator d-lg-none position-absolute top-0 start-100 translate-middle">
            !
          </span>
        )}
      </div>
    );

    return filterButton;
  }

  public override renderFilterBadges() {
    const filterBadgeVisible = this.props.selectedCategory !== undefined;

    const filtermenu = filterBadgeVisible && (
      <FilterBadge
        text={
          this.props.selectedCategory === null ?
            this.context.formatMessage(messages.noCategories)
          : (this.props.selectedCategory ?? "")
        }
        type={this.context.formatMessage(messages.filterPreamble)}
        onClear={this.props.onClearFilter}
        onOpen={this.props.onShowFilter}
      />
    );
    return filtermenu || <></>;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(TrendSiteBarComponent));
